import React, { useEffect, useState } from "react";
import instance from "../instance";
import { useNavigate, useLocation } from "react-router-dom";
import CustomerSearch from "./Customers/CustomerSearch";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatDate2 } from "./Customers/utils";
import { openConfirmPhone } from "./Customer/PhoneSms";

const PartnerPhone = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhones, setSelectedPhones] = useState([]);
  const [customerData, setCustomerData] = useState([]);

  const [sortConfig, setSortConfig] = useState({
    key: "LastLogin",
    direction: "descending",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [excludeTerm, setExcludeTerm] = useState("");
  const [roleNameFilterActive, setRoleNameFilterActive] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    Promise.all([instance.get("partner/phone"), instance.get("merged")])
      .then(([partnerResponse, customerResponse]) => {
        setData(partnerResponse.data);
        setCustomerData(customerResponse.data);
        setLoading(false);
      })
      .catch((error) => {
        setError(error.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const nameFilter = queryParams.get("name");
    if (nameFilter) {
      setSearchTerm(nameFilter);
    }
  }, [location.search]);

  const handleSort = (column) => {
    const direction =
      sortConfig.direction === "ascending" ? "descending" : "ascending";
    if (column === "sequence") {
      setSortConfig({ key: "ID", direction });
    } else {
      setSortConfig({ key: column, direction });
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleExcludeSearch = (event) => {
    setExcludeTerm(event.target.value);
  };

  const resetSearch = () => {
    setSearchTerm("");
    setExcludeTerm("");
    setRoleNameFilterActive(false);
  };

  const toggleRoleNameFilter = () => {
    setRoleNameFilterActive((prevState) => !prevState);
  };

  const getAllUniqueValues = () => {
    const uniqueValues = new Set();
    data.forEach((item) => {
      Object.values(item).forEach((value) => {
        if (value instanceof Date || !isNaN(Date.parse(value))) {
          uniqueValues.add(formatDateToCustomString(value));
        } else {
          uniqueValues.add(value);
        }
      });
    });
    return Array.from(uniqueValues).sort((a, b) => (a > b ? -1 : 1));
  };

  const formatDateToCustomString = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours() % 12 || 12).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const ampm = date.getHours() >= 12 ? "PM" : "AM";

    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds} ${ampm}`;
  };
  const sortedData = React.useMemo(() => {
    let sorted = [...data];

    if (roleNameFilterActive) {
      sorted = sorted.filter(
        (item) => item.RoleName && item.RoleName.trim() !== ""
      );
    }

    sorted = sorted.filter((item) => {
      const searchTermLower = searchTerm.toLowerCase();
      const excludeTermLower = excludeTerm.toLowerCase();

      // Convert dates to a comparable format
      const formatDate = (dateString) => {
        if (!dateString) return "";
        return formatDateToCustomString(dateString).toLowerCase();
      };

      const itemValues = [
        item.PartnerName,
        item.Phone1,
        item.Phone2,
        item.NameDatabase,
        item.RoleName,
        formatDate(item.LastLogin),
        formatDate(item.LastLogout),
      ].map((value) => (value ? value.toLowerCase() : ""));

      const matchesSearchTerm =
        searchTerm === "" ||
        itemValues.some((value) => value.startsWith(searchTermLower));
      const matchesExcludeTerm =
        excludeTerm === "" ||
        !itemValues.some((value) => value.startsWith(excludeTermLower));

      return matchesSearchTerm && matchesExcludeTerm;
    });

    sorted.sort((a, b) => {
      if (["LastLogin", "LastLogout"].includes(sortConfig.key)) {
        const dateA = a[sortConfig.key]
          ? new Date(a[sortConfig.key])
          : new Date(0);
        const dateB = b[sortConfig.key]
          ? new Date(b[sortConfig.key])
          : new Date(0);
        if (dateA < dateB) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (dateA > dateB) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      } else if (sortConfig.key === "RoleName") {
        const roleNameA = a.RoleName ? a.RoleName.toLowerCase() : "";
        const roleNameB = b.RoleName ? b.RoleName.toLowerCase() : "";
        if (roleNameA < roleNameB) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (roleNameA > roleNameB) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      } else {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      }
    });

    return sorted.map((item, index) => ({ ...item, sequence: index + 1 }));
  }, [data, sortConfig, searchTerm, excludeTerm, roleNameFilterActive]);

  if (error) {
    return <div>Error: {error}</div>;
  }

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);

    const options = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    };

    const dateStringFormatted = date.toLocaleDateString("en-GB", options);

    return `${dateStringFormatted}`;
  };

  const getLoginCellColor = (loginTime) => {
    if (!loginTime) return "transparent";
    const now = new Date();
    const loginDate = new Date(loginTime);
    const past8Hours = new Date(now.getTime() - 12 * 60 * 60 * 1000);
    const past7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    if (loginDate >= past8Hours && loginDate <= now) {
      return "rgba(8, 171, 14, 0.3)";
    } else if (loginDate < past7Days) {
      return "rgba(255, 0, 0, 0.2)";
    } else {
      return "transparent";
    }
  };

  const handleRunTask = async () => {
    try {
      await instance.post("phone/run-partner");
      toast.success("Task executed successfully.");

      // Wait for 5 seconds before reloading the page
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    } catch (error) {
      console.error("Error executing task:", error);
      toast.error("Error executing task: " + error.message);
    }
  };

  const handleSelectPhone = (phone) => {
    setSelectedPhones((prev) =>
      prev.includes(phone) ? prev.filter((p) => p !== phone) : [...prev, phone]
    );
  };
  const selectedCount = selectedPhones.length;

  const handleSelectAllPhones = () => {
    if (selectedPhones.length === sortedData.length) {
      setSelectedPhones([]);
    } else {
      setSelectedPhones(sortedData.map((item) => item.Phone1));
    }
  };

  const handleSendSms = () => {
    openConfirmPhone(selectedPhones);
  };

  function capitalizeName(name) {
    // Capitalize the first letter of the name
    name = name.charAt(0).toUpperCase() + name.slice(1);

    // Regex to find "El" or "Al" and capitalize the next character
    const regex = /\b(El|Al)([a-z])/g;
    name = name.replace(regex, function (match, p1, p2) {
      return p1 + p2.toUpperCase();
    });

    return name;
  }

  const handleSendWhatsApp = () => {
    selectedPhones.forEach((phone) => {
      const databaseItem = sortedData.find((item) => item.Phone1 === phone);

      if (databaseItem) {
        // تحويل اسم القاعدة إلى حروف صغيرة قبل المقارنة
        const databaseNameLower = databaseItem.NameDatabase.toLowerCase();

        const customerItem = customerData.find(
          (customer) => customer.Name.toLowerCase() === databaseNameLower
        );

        if (customerItem) {
          // تعديل هنا لاستخدام اسم المستخدم مع تحويله
          const capitalized_name = capitalizeName(customerItem.Name);
          const countryCode = "2";
          const priceType = customerItem.PriceType || "";

          let priceTypeString = "";
          if (priceType === "Bronze") {
            priceTypeString = "البرونزي";
          } else if (priceType === "Silver") {
            priceTypeString = "الفضي";
          } else if (priceType === "Golden") {
            priceTypeString = "الذهبي";
          } else if (priceType === "Platinum") {
            priceTypeString = "البلاتيني";
          } else if (priceType === "Diamond") {
            priceTypeString = "الماسي";
          }

          // إنقاص يوم واحد من التاريخ
          const paymentDate = new Date(customerItem.PaymentDate);
          paymentDate.setDate(paymentDate.getDate() - 1);
          const formattedDate = formatDate2(paymentDate);

          // استخدام الاسم المحول هنا في الرسالة
          const message = `عزيزي العميل،
تم تجديد اشتراك الباقة ${priceTypeString} لنظام ${capitalized_name} بنجاح.
  
صالحة حتى تاريخ ${formattedDate}. نحن نقدر ثقتكم في خدماتنا.
  
في حال كان لديكم أي استفسارات، تحتاجون إلى مساعدة أو معلومات إضافية، فلا تترددوا في التواصل مع فريق الدعم لدينا.
  
نحن هنا دائمًا لضمان حصولكم على خدمات عالية الجودة وتلبية احتياجاتكم بكفاءة.
  
شكرًا لاستمراركم معنا كجزء من عالم OwlTech. نتطلع دائمًا لتقديم أفضل الخدمات لكم.
  
مع أطيب التحيات،
أوول تك | OwlTech
          https://www.owltecheg.com
          +201006375557
          Info@owltecheg.com`;

          const fullPhoneNumber = `${countryCode}${phone}`;

          const isMobile = /iPhone|iPad|iPod|Android/i.test(
            navigator.userAgent
          );
          const url = isMobile
            ? `https://wa.me/${fullPhoneNumber}?text=${encodeURIComponent(
                message
              )}`
            : `https://web.whatsapp.com/send?phone=${fullPhoneNumber}&text=${encodeURIComponent(
                message
              )}`;

          window.open(url, "_blank");
        } else {
          console.error(
            `No matching customer found for ${databaseItem.NameDatabase}`
          );
        }
      }
    });
  };

  // Function to export to CSV
  const exportToCSV = () => {
    const csvRows = [];
    const headers = ["PartnerName", "Phone1"];
    csvRows.push(headers.join(","));

    sortedData.forEach((item) => {
      const row = [item.PartnerName, item.Phone1];
      csvRows.push(row.join(","));
    });

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "partner_phones.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to export to vCard
  const exportToVCard = () => {
    let vCardContent = "";

    sortedData.forEach((item) => {
      vCardContent += `BEGIN:VCARD\nVERSION:3.0\nFN:${item.PartnerName}\nTEL:${item.Phone1}\nEND:VCARD\n`;
    });

    const vCardBlob = new Blob([vCardContent], { type: "text/vcard" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(vCardBlob);
    link.setAttribute("download", "partner_phones.vcf");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ---------------------------------------------------------------------------------------------------------------------

  return (
    <div>
      <header>
        <h1>Partner Name</h1>
      </header>
      <div style={{ textAlign: "center" }}>
        <button onClick={() => navigate("/")} className="button">
          ← Main
        </button>
        <button className="button" onClick={handleRunTask}>
          Run Task
        </button>

        <button className="button" onClick={exportToCSV}>
          Export CSV
        </button>
        <button className="button" onClick={exportToVCard}>
          Export vCard
        </button>

        <button className="button" onClick={toggleRoleNameFilter}>
          {roleNameFilterActive ? "All Roles" : " Empty Roles"}
        </button>
        <button className="button" onClick={handleSendSms}>
          Send SMS
        </button>
        <button className="button" onClick={handleSendWhatsApp}>
          Send WhatsApp
        </button>
      </div>

      <CustomerSearch
        searchTerm={searchTerm}
        excludeTerm={excludeTerm}
        datalistData={getAllUniqueValues()}
        handleSearch={handleSearch}
        handleExcludeSearch={handleExcludeSearch}
        resetSearch={resetSearch}
        dateFilter={null}
        formatDateRange={() => ""}
      />
      <table id="partner-phone-table">
        <thead>
          <tr>
            <th className="sortable" onClick={() => handleSort("sequence")}>
              #
            </th>
            <th className="sortable" onClick={() => handleSort("PartnerName")}>
              Partner Name
            </th>
            <th className="sortable" onClick={() => handleSort("Phone1")}>
              Phone 1
            </th>
            <th className="sortable" onClick={() => handleSort("Phone2")}>
              Phone 2
            </th>
            <th className="sortable" onClick={() => handleSort("NameDatabase")}>
              Name
            </th>
            <th className="sortable" onClick={() => handleSort("RoleName")}>
              Role Name
            </th>
            <th className="sortable" onClick={() => handleSort("LastLogin")}>
              Last Login
            </th>
            <th className="sortable" onClick={() => handleSort("LastLogout")}>
              Last Logout
            </th>
            <th>
              <label htmlFor="all">
                <span>({selectedCount} selected)</span>
              </label>
              <input
                id="all"
                type="checkbox"
                onChange={handleSelectAllPhones}
                checked={selectedPhones.length === sortedData.length}
              />
            </th>
          </tr>
        </thead>
        <tbody className="TOTAL">
          {loading ? (
            <tr>
              <td colSpan="8">Loading...</td>
            </tr>
          ) : sortedData.length === 0 ? (
            <tr>
              <td colSpan="8">No data available</td>
            </tr>
          ) : (
            sortedData.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item.PartnerName}</td>
                <td>{item.Phone1}</td>
                <td>{item.Phone2}</td>
                <td>{item.NameDatabase}</td>
                <td>{item.RoleName}</td>
                <td
                  style={{
                    backgroundColor: getLoginCellColor(item.LastLogin),
                  }}
                >
                  {formatDate(item.LastLogin)}
                </td>
                <td>{formatDate(item.LastLogout)}</td>
                <td>
                  <input
                    style={{ cursor: "pointer" }}
                    type="checkbox"
                    checked={selectedPhones.includes(item.Phone1)}
                    onChange={() => handleSelectPhone(item.Phone1)}
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <footer>
        <span>© 2025 OwlTech All rights reserved.</span>
      </footer>
    </div>
  );
};

export default PartnerPhone;
